<template>
  <v-card class="h-100" elevation="0" rounded="lg">
    <div v-if="widgetData.loading" class="pa-6">
      <v-skeleton-loader type="heading" class="mb-2 bg-transparent" width="150" />
      <v-divider class="border-opacity-100 mb-6" color="indigo-faint" />
      <v-row>
        <v-col cols="3">
          <v-skeleton-loader type="heading" class="mb-2 bg-transparent" height="40" />
          <v-skeleton-loader type="text" class="bg-transparent" width="100" />
          <v-skeleton-loader type="text" class="bg-transparent" width="120" />
        </v-col>
        <v-col cols="9">
          <v-skeleton-loader type="subtitle" class="mb-4 bg-transparent" width="200" />
          <v-skeleton-loader
            v-for="i in 4"
            :key="i"
            type="list-item-two-line"
            class="bg-transparent mb-2"
          />
        </v-col>
      </v-row>
    </div>

    <template v-else-if="widgetData.data">
      <v-card-title class="d-flex align-center px-6 py-3">
        <span class="subtitle1">{{ $t("coroImpact.widgets.ticketsSpotlightWidget.title") }}</span>
        <v-spacer></v-spacer>
        <a class="body2 coro-link text-decoration-none" @click="navigateToTickets()">
          <div class="view-all-btn body2 coro-link item-clickable d-flex align-center">
            <span>{{ $t("general.viewAll") }}</span>
            <v-icon icon="$chevronRight" color="link" size="16" />
          </div>
        </a>
      </v-card-title>
      <v-divider class="border-opacity-100" color="indigo-faint" />

      <v-card-text class="pb-6 px-6">
        <v-row>
          <v-col
            cols="3"
            class="d-flex flex-column border-e border-indigo-faint border-opacity-100"
          >
            <div class="total-count text-primary mb-2">
              {{ widgetData.data.total }}
            </div>
            <p class="subtitle2 mb-2">
              {{ $t("coroImpact.widgets.ticketsSpotlightWidget.totalGenerated") }}
            </p>
            <span class="body3" :class="delta.cssClass" v-html="delta.text"></span>
          </v-col>

          <v-col cols="9">
            <p class="subtitle2 mb-4 pl-6">
              {{
                context === "single-view"
                  ? $t("coroImpact.widgets.ticketsSpotlightWidget.mostCommonTicketType")
                  : $t(
                      "coroImpact.widgets.ticketsSpotlightWidget.mostCommonTicketTypeAcrossWorkspaces"
                    )
              }}
            </p>
            <v-list class="pa-0" lines="one" bg-color="transparent">
              <template
                v-for="(item, index) in widgetData.data.eventTriggers"
                :key="item.eventTrigger"
              >
                <v-list-item class="px-6 py-1" @click="navigateToTickets(item)">
                  <div class="d-flex align-center">
                    <v-icon size="40" :icon="getIconName(item)" class="mr-4"></v-icon>
                    <v-list-item-title class="body2">
                      <div class="d-flex flex-column">
                        <div>
                          {{ $t(`tickets.eventTriggers.${item.eventTrigger}`) }}
                        </div>
                        <div
                          v-if="context === 'msp' && item.workspacesCount"
                          class="body3 text-indigo-medium"
                        >
                          {{
                            $t(
                              "general.workspaces",
                              { n: item.workspacesCount },
                              item.workspacesCount
                            )
                          }}
                        </div>
                      </div>
                    </v-list-item-title>
                  </div>

                  <template v-slot:append>
                    <div class="d-flex align-baseline">
                      <span class="subtitle2 mr-1">{{ item.count }}</span>
                      <span class="body2 text-indigo-medium">({{ item.percentile }}%)</span>
                    </div>
                  </template>
                </v-list-item>
                <v-divider
                  v-if="index !== widgetData.data.eventTriggers.length - 1"
                  class="border-opacity-100 my-1"
                  color="indigo-faint"
                />
              </template>
            </v-list>
          </v-col>
        </v-row>
      </v-card-text>
    </template>

    <div v-else class="h-100 d-flex align-center justify-center flex-column pa-6">
      <v-icon icon="$alertTriangle" size="80" color="grey-lighten-1"></v-icon>
      <span class="subtitle1 mb-4 mt-4">
        {{ $t(`coroImpact.widgets.ticketsSpotlightWidget.error`) }}
      </span>
      <div
        class="coro-link"
        data-testid="retry-fetch-data-button"
        @click="$emit('retry-fetch-data')"
      >
        {{ $t("general.retry") }}
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { defineComponent, type PropType, computed } from "vue";
import type {
  TicketsSpotlightWidgetData,
  TicketsSpotlightWidgetDataItem,
} from "@/_store/coro-impact/coro-impact.module.ts";
import type { DataValue, TimeFrame } from "@/types.ts";
import { TicketStatus, ticketTriggerIcons, ticketTypeIcons } from "@/constants/tickets.ts";
import { RouteName } from "@/constants/routes.ts";
import { useRouter } from "vue-router";
import { FilterContext, useFiltersStore } from "@/_store/filters.module.ts";
import { useI18n } from "vue-i18n";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction, GTMParams } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";

export default defineComponent({
  props: {
    widgetData: {
      type: Object as PropType<DataValue<TicketsSpotlightWidgetData>>,
      required: true,
    },
    context: {
      type: String as PropType<"single-view" | "msp">,
      default: "single-view",
    },
    filterTimeFrame: {
      type: Object as PropType<TimeFrame>,
      required: true,
    },
  },
  emits: ["retry-fetch-data"],
  setup(props) {
    const router = useRouter();
    const filtersStore = useFiltersStore();
    const i18n = useI18n();

    const getIconName = ({ eventTrigger, eventType }: TicketsSpotlightWidgetDataItem) => {
      const icon = ticketTypeIcons[eventType] || ticketTriggerIcons[eventTrigger];
      return `$${icon}`;
    };

    const targetRoute = computed(() => {
      return props.context === "msp" ? RouteName.MSP_TICKETS_PAGE : RouteName.TICKETS;
    });

    const navigateToTickets = (item?: TicketsSpotlightWidgetDataItem) => {
      const gtmData = item ? startCase(item.eventTrigger) : "View All";
      trackGtmEvent(
        GtmAction.CORO_INSIGHTS_PAGE,
        `${startCase(props.context)} - ${GTMParams.CORO_INSIGHTS_TICKETS_WIDGET} - ${gtmData}`
      );

      const context = props.context === "msp" ? FilterContext.MSP_TICKETS : FilterContext.TICKETS;
      filtersStore.setFilters(context, {
        eventTimeRange: {
          ...props.filterTimeFrame,
        },
        status: TicketStatus.ALL,
        eventTriggers: item ? [item.eventTrigger] : [],
      });
      return router.push({ name: targetRoute.value });
    };

    const delta = computed(() => {
      if (!props.widgetData.data) {
        return { text: "", cssClass: "" };
      }
      const value = props.widgetData.data.delta;
      if (value < 0) {
        return {
          text: `&#8595; ${Math.abs(value)}% ${i18n.t("coroImpact.comparedWithPreviousPeriod")}`,
          cssClass: "text-green-dark",
        };
      }
      if (value > 0) {
        return {
          text: `&#8593; ${Math.abs(value)}% ${i18n.t("coroImpact.comparedWithPreviousPeriod")}`,
          cssClass: "text-red-dark",
        };
      }
      return {
        text: i18n.t("coroImpact.noChangeComparedToPreviousPeriod"),
        cssClass: "text-indigo-medium",
      };
    });

    return {
      navigateToTickets,
      getIconName,
      delta,
    };
  },
});
</script>

<style scoped lang="scss">
.total-count {
  font-family: "Quicksand", sans-serif;
  font-size: 48px;
  font-style: normal;
  font-weight: 500;
  line-height: 105%; /* 50.4px */
  letter-spacing: -0.6px;
}

.view-all-btn {
  text-decoration: none !important;
  user-select: none;
  .icon-chevron-right-xs {
    text-decoration: none !important;
    &:before {
      color: rgb(var(--v-theme-anchor-base)) !important;
    }
  }

  &:hover span {
    text-decoration: underline !important; /* Underline only the text */
  }
}
</style>
